import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import {
  FaAward,
  FaCheckCircle,
  FaCoins,
  FaExclamationTriangle,
  FaGift,
  FaInfoCircle,
  FaReceipt,
  FaSlidersH,
  FaSyncAlt,
  FaWallet,
} from "react-icons/fa";
import { apiSeedGetConfig, apiSeedUpdateConfig } from "../api/index";
import { SettingsEmbedContext } from "../components/SettingsPanel";
import { AppButton } from "../components/ui";
import SettingHelp from "./Settings/settingsHelp";
import { ReadOnlyNotice } from "./Settings/settingsUi";
import "./SeedConfigView.css";

/* The three amounts the Seed programme reads (server SEED_CONFIG_KEYS in
   services/seedConfigService.js). The vendor reward and the delivery cost band
   that used to sit here were retired on 1 Nov 2026: nothing read them, and the
   band duplicated the delivery fee engine. Defaults come from the server's
   `defaults`, never from this file. */
export const FIELD_GROUPS = [
  {
    key: "rewards",
    eyebrow: "Earning credit",
    title: "What a seeder earns on approval",
    help: "seedCreditProgramme",
    description:
      "Paid into the seeder's Seed Credit balance when operations approves their submission. An approved vendor itself earns the seeder free delivery from that vendor, not a shilling amount.",
    descriptionHelp: "seedVendorReward",
    icon: FaAward,
    fields: [
      {
        key: "productApprovedUgx",
        label: "Reward per approved product",
        shortLabel: "Per approved product",
        hint: "Paid for each product that passes quality control in an approved submission.",
        help: "seedProductReward",
        icon: FaCoins,
      },
      {
        key: "milestone10BonusUgx",
        label: "Bonus for every 10 vendors in 30 days",
        shortLabel: "10-vendor bonus",
        hint: "Paid on the approval that brings the seeder's approvals in the last 30 days to 10, 20, 30 and so on.",
        help: "seedMilestoneBonus",
        icon: FaGift,
      },
    ],
  },
  {
    key: "spending",
    eyebrow: "Spending credit",
    title: "Limit on one order",
    description:
      "No checkout spends Seed Credit yet, so this limit changes no order today. It applies once credit can be put towards a delivery fee.",
    icon: FaWallet,
    fields: [
      {
        key: "perOrderRedemptionCapUgx",
        label: "Most credit one order can use",
        shortLabel: "Most per order",
        hint: "The largest amount of Seed Credit a customer may put towards one order's delivery fee.",
        help: "seedPerOrderCap",
        icon: FaReceipt,
      },
    ],
  },
];

const FIELDS = FIELD_GROUPS.flatMap((group) => group.fields);

const fmtUgx = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "Not set";
  return new Intl.NumberFormat("en-UG", {
    style: "currency",
    currency: "UGX",
    maximumFractionDigits: 0,
  }).format(Math.round(number));
};

const buildDraft = (persisted) =>
  FIELDS.reduce((result, field) => {
    const value = persisted?.[field.key];
    result[field.key] =
      value !== undefined && value !== null ? String(value) : "";
    return result;
  }, {});

export default function SeedConfigView({ canEdit = true }) {
  const embedded = useContext(SettingsEmbedContext);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [persisted, setPersisted] = useState(null);
  const [resolved, setResolved] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [draft, setDraft] = useState({});

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const { data } = await apiSeedGetConfig();
      const databaseConfig = data.config || null;
      setPersisted(databaseConfig);
      setResolved(data.resolvedConfig || databaseConfig || null);
      setDefaults(data.defaults || {});
      setDraft(buildDraft(databaseConfig));
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load the Seed credit amounts.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changedPayload = useMemo(() => {
    const changes = {};
    FIELDS.forEach((field) => {
      const raw = draft[field.key];
      if (raw === "" || raw === undefined) return;
      const number = Number(raw);
      if (!Number.isFinite(number)) return;
      const current = persisted?.[field.key];
      if (
        current === undefined ||
        current === null ||
        Number(current) !== number
      ) {
        changes[field.key] = number;
      }
    });
    return changes;
  }, [draft, persisted]);

  const clientValidation = useMemo(() => {
    for (const field of FIELDS) {
      const raw = draft[field.key];
      if (raw === "" || raw === undefined) continue;
      const number = Number(raw);
      if (!Number.isFinite(number) || number < 0) {
        return `${field.label} must be a non-negative amount.`;
      }
    }

    return null;
  }, [draft]);

  const changedFields = useMemo(
    () =>
      FIELDS.filter((field) =>
        Object.prototype.hasOwnProperty.call(changedPayload, field.key)
      ),
    [changedPayload]
  );

  const anyChanges = changedFields.length > 0;
  const isDatabaseManaged = Boolean(persisted);

  const fieldEffectiveValue = (field) =>
    resolved?.[field.key] ?? persisted?.[field.key] ?? defaults?.[field.key];

  const fieldPreviewValue = (field) => {
    const raw = draft[field.key];
    if (raw !== "" && Number.isFinite(Number(raw))) return Number(raw);
    return fieldEffectiveValue(field);
  };

  const handleSave = async () => {
    if (clientValidation) {
      setError(clientValidation);
      return;
    }
    if (!anyChanges) {
      setSuccessMsg("No changes to save.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const { data } = await apiSeedUpdateConfig(changedPayload);
      const databaseConfig = data.config;
      setPersisted(databaseConfig);
      setResolved(data.resolvedConfig || databaseConfig);
      if (data.defaults) setDefaults(data.defaults);
      setDraft(buildDraft(databaseConfig));
      setSuccessMsg("Saved. The next approval uses these amounts.");
    } catch (err) {
      setError(err?.response?.data?.message || "Could not save the Seed credit amounts.");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setDraft(buildDraft(persisted));
    setSuccessMsg(null);
    setError(null);
  };

  if (loading) {
    return (
      <div className={`content seed-config-workspace${embedded ? " is-embedded" : ""}`}>
        <div className="seed-config-loading">
          <Spinner animation="border" />
          <strong>Loading the Seed credit amounts</strong>
          <span>Reading the saved amounts and the server defaults.</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`content seed-config-workspace${embedded ? " is-embedded" : ""}`}>
      {!embedded && (
        <header className="seed-config-header">
          <div className="seed-config-header__title">
            <span className="seed-config-header__eyebrow">Support &amp; programmes</span>
            <h1>
              <span>
                <FaSlidersH aria-hidden="true" />
              </span>
              Seed credits
            </h1>
            <p>What seeders earn when operations approves their submissions, and the limit on spending it.</p>
          </div>
        </header>
      )}

      {!canEdit && <ReadOnlyNotice level="update" />}

      <section className="seed-config-metrics" aria-label="Current key values">
        {[
          ["productApprovedUgx", "Per approved product", FaCoins, "blue"],
          ["milestone10BonusUgx", "10-vendor bonus", FaGift, "gold"],
          ["perOrderRedemptionCapUgx", "Most credit per order", FaReceipt, "red"],
        ].map(([key, label, Icon, tone]) => (
          <div className={`seed-config-metric is-${tone}`} key={key}>
            <span className="seed-config-metric__icon">
              <Icon aria-hidden="true" />
            </span>
            <span>
              <small>{label}</small>
              <strong>{fmtUgx(resolved?.[key])}</strong>
            </span>
          </div>
        ))}
      </section>

      {error && (
        <Alert variant="danger" className="seed-config-alert">
          <FaExclamationTriangle aria-hidden="true" />
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)}>
            Dismiss
          </button>
        </Alert>
      )}

      {successMsg && (
        <Alert variant="success" className="seed-config-alert">
          <FaCheckCircle aria-hidden="true" />
          <span>{successMsg}</span>
          <button type="button" onClick={() => setSuccessMsg(null)}>
            Dismiss
          </button>
        </Alert>
      )}

      {!persisted && (
        <div className="seed-config-runtime-notice">
          <FaInfoCircle aria-hidden="true" />
          <div>
            <strong>Using the server defaults</strong>
            <p>
              Nothing has been saved on this page yet, so the amounts below are
              the server's defaults. The first save stores all three, and from
              then on the saved amounts apply.
            </p>
          </div>
        </div>
      )}

      <div className="seed-config-layout">
        <main className="seed-config-editor">
          {FIELD_GROUPS.map((group) => {
            const GroupIcon = group.icon;
            return (
              <section className="seed-config-section" key={group.key}>
                <div className="seed-config-section__heading">
                  <span className="seed-config-section__icon">
                    <GroupIcon aria-hidden="true" />
                  </span>
                  <div>
                    <span>{group.eyebrow}</span>
                    <h2>
                      {group.title}
                      {group.help && <SettingHelp topic={group.help} />}
                    </h2>
                    <p>
                      {group.description}
                      {group.descriptionHelp && <SettingHelp topic={group.descriptionHelp} />}
                    </p>
                  </div>
                </div>

                <div className="seed-config-fields">
                  {group.fields.map((field) => {
                    const FieldIcon = field.icon;
                    const raw = draft[field.key] ?? "";
                    const number = Number(raw);
                    const isChanged = Object.prototype.hasOwnProperty.call(
                      changedPayload,
                      field.key
                    );
                    const invalid =
                      raw !== "" &&
                      (!Number.isFinite(number) || number < 0);

                    return (
                      <div
                        className={`seed-config-field${
                          isChanged ? " is-modified" : ""
                        }${invalid ? " is-invalid" : ""}`}
                        key={field.key}
                      >
                        <span className="seed-config-field__icon">
                          <FieldIcon aria-hidden="true" />
                        </span>
                        <div className="seed-config-field__copy">
                          <div>
                            <label htmlFor={field.key}>
                              {field.label}
                              <SettingHelp topic={field.help} />
                            </label>
                            {isChanged && <span>Modified</span>}
                          </div>
                          <p>
                            {field.hint}
                            {defaults?.[field.key] !== undefined && ` Default ${fmtUgx(defaults[field.key])}.`}
                          </p>
                        </div>
                        <div className="seed-config-field__control">
                          <div>
                            <span>UGX</span>
                            <input
                              id={field.key}
                              type="number"
                              min="0"
                              step="100"
                              placeholder={String(fieldEffectiveValue(field) ?? "")}
                              value={raw}
                              disabled={!canEdit || saving}
                              onChange={(event) => {
                                setDraft((current) => ({
                                  ...current,
                                  [field.key]: event.target.value,
                                }));
                                setSuccessMsg(null);
                              }}
                              aria-invalid={invalid}
                            />
                          </div>
                          <small>
                            {invalid
                              ? "Enter a non-negative amount"
                              : raw === ""
                              ? `In use: ${fmtUgx(fieldEffectiveValue(field))}`
                              : fmtUgx(number)}
                          </small>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </main>

        <aside className="seed-config-summary">
          <section className="seed-config-summary__status">
            <div className="seed-config-summary__heading">
              <span className="seed-config-summary__icon">
                <FaSyncAlt aria-hidden="true" />
              </span>
              <div>
                <span>
                  Where these come from
                  <SettingHelp topic="seedConfigSource" />
                </span>
                <h2>
                  {isDatabaseManaged ? "Saved on this page" : "Server defaults"}
                </h2>
              </div>
            </div>
            <p>
              A save takes effect on the next approval, without a restart.
              Credit already paid is not recalculated.
            </p>
          </section>

          <section className="seed-config-summary__values">
            <div className="seed-config-summary__section-title">
              <span>Amounts after saving</span>
              <strong>
                {changedFields.length
                  ? `${changedFields.length} modified`
                  : "No changes"}
              </strong>
            </div>
            <div className="seed-config-value-list">
              {FIELDS.map((field) => {
                const isChanged = Object.prototype.hasOwnProperty.call(
                  changedPayload,
                  field.key
                );
                return (
                  <div
                    className={isChanged ? "is-modified" : ""}
                    key={field.key}
                  >
                    <span>{field.shortLabel}</span>
                    <strong>{fmtUgx(fieldPreviewValue(field))}</strong>
                  </div>
                );
              })}
            </div>
          </section>

          {clientValidation && (
            <div className="seed-config-validation">
              <FaExclamationTriangle aria-hidden="true" />
              <span>{clientValidation}</span>
            </div>
          )}

          {canEdit && (
            <div className="seed-config-actions">
              <AppButton
                icon="fas fa-save"
                loading={saving}
                onClick={handleSave}
                disabled={!anyChanges || Boolean(clientValidation)}
              >
                {saving ? "Saving" : "Save changes"}
              </AppButton>
              <AppButton
                variant="quiet"
                icon="fas fa-undo"
                onClick={handleReset}
                disabled={saving || !anyChanges}
              >
                Discard changes
              </AppButton>
              <AppButton
                variant="quiet"
                icon={refreshing ? "fas fa-sync fa-spin" : "fas fa-sync"}
                onClick={() => load(true)}
                disabled={saving || refreshing}
              >
                {refreshing ? "Reloading" : "Reload saved amounts"}
              </AppButton>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
